<script lang="ts" setup>
import useSearchStore from "~/shared/store/modules/search";
import useAccountStore from "~/shared/store/modules/account";
import customFetch from "~/composables/custom-fetch";
import AppInput from "~/components/ui/form/formElements/AppInput.vue";
import ModalFormPolicy from "~/components/ui/form/ModalFormPolicy.vue";
import AppButton from "~/components/ui/buttons/AppButton.vue";
import apiConfig from "~/shared/config/api";
import useMainStore from "~/shared/store";
import type {IInput} from "~/shared/interfaces/ui/form/formInputs";
import type {IOneClickRequest, IProductInfo} from "~/shared/interfaces/search/product";
import type {IValidationData} from "~/shared/interfaces/validator";

type fieldType = 'firstName' | 'lastName' | 'phone' | 'email'

const props = defineProps<{
  product: IProductInfo
  minPrice: number
}>()

const { product } = apiConfig
const mainStore = useMainStore()
const searchStore = useSearchStore()
const isSubmitDisabled = ref(true)
const changeableFields: fieldType[] = ['firstName' , 'lastName' , 'phone' , 'email']
const user = useAccountStore().userProfile?.user
const formFields = ref<IInput[]>([
  {
    inputConfig: {
      label: 'Фамилия',
      id: 'lastName',
      interactiveLabel: true,
      defaultValue: user?.lastName
    },
    validatorConfig: {
      required: true,
    }
  },
  {
    inputConfig: {
      label: 'Имя',
      id: 'firstName',
      interactiveLabel: true,
      defaultValue: user?.firstName
    },
    validatorConfig: {
      required: true,
    }
  },
  {
    inputConfig: {
      label: 'E-mail',
      id: 'email',
      interactiveLabel: true,
      defaultValue: user?.email
    },
    validatorConfig: {
      required: true,
      emailFormat: true,
    },
  },
  {
    inputConfig: {
      label: 'Телефон',
      id: 'phone',
      interactiveLabel: true,
      maskType: 'phone',
      defaultValue: user?.phone
    },
    validatorConfig: {
      required: true,
      phoneFormat: true,
    },
  },
])
const fieldsWithErrors = ref<fieldType[]>([])

const formState = ref<IOneClickRequest>({
  firstName: '',
  lastName: '',
  email: '',
  uuid: useRoute().params.id as string,
  brand: props.product.brand.name,
  article: props.product.article,
  citySlug: mainStore.sotransCitySelected?.slug || 'spb',
  phone: '',
  price: props.minPrice
})

async function sendBuyRequest() {
  searchStore.setOneClickModalState(false)
  searchStore.setSuccessModalType('oneClick')
  searchStore.setSuccessModalState(true)
  const res = await customFetch(product.buyOneClickRequest(formState.value))
  if(!import.meta.env.DEV) window.ym(89928957,'reachGoal','product-modal', {order_price: formState.value.price, currency: 'RUB'})
  // TODO error handling
}

function checkFields() {
  fieldsWithErrors.value = []

  changeableFields.forEach(el => {
    if (!formState.value[el as fieldType] || formState.value[el as fieldType] === 'error') {
      fieldsWithErrors.value.push(el)
    } else {
      fieldsWithErrors.value.filter(field => field === el)
    }
  })

  !fieldsWithErrors.value.length ? isSubmitDisabled.value = false : isSubmitDisabled.value = true
}

function handleInput(event: IValidationData) {
  if (formState.value && event.errorInfo.hasError) {
    formState.value[event.id as fieldType] = 'error'
  } else {
    formState.value[event.id as fieldType] = event.value
  }
  checkFields()
}

onMounted(() => {
  if (user) {
    checkFields()
  }
})
</script>

<template>
  <form
    class="form"
    @submit.prevent
  >
    <div class="form__fields">
      <AppInput
        v-for="(field, index) in formFields"
        :key="`one-click-field-${index}`"
        :input-config="field.inputConfig"
        :validator-settings="field.validatorConfig"
        @get-data="handleInput($event)"
      />
    </div>
    <ModalFormPolicy/>
    <div class="form__submit">
      <AppButton
        text="Отправить заявку"
        @click="sendBuyRequest"
        :is-disabled="isSubmitDisabled"
        :is-submit="true"
      />
    </div>
  </form>
</template>

<style lang="scss" scoped>
.form {
  @include xs-breakpoint {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
  }
  &__fields {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  &__submit {
    margin-top: 28px;
    @include xs-breakpoint {
      margin-top: auto;
      margin-bottom: 24px;
    }
    button {
      width: 50%;
      @include xs-breakpoint {
        width: 100%;
      }
    }
  }
}
</style>
